import { useEffect, useState } from 'react'
import { Building2, ChevronDown, FolderKanban, Check, Loader2, Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useOrganizationStore } from '@/stores/organization-store'
import { useAuthStore } from '@/stores/auth-store'
import type { Organization, Project } from '@/schemas/organization'
import { CreateProjectDialog } from './create-project-dialog'

export function OrganizationSwitcher() {
  const { t } = useTranslation('common')
  const { t: tOrg } = useTranslation('organizations')
  const { isAuthenticated } = useAuthStore()
  const {
    organizations,
    currentOrganization,
    projects,
    currentProject,
    isLoading,
    isInitialized,
    initializeContext,
    setCurrentOrganization,
    setCurrentProject,
  } = useOrganizationStore()
  const [showCreateProject, setShowCreateProject] = useState(false)

  // Initialize context when authenticated
  useEffect(() => {
    if (isAuthenticated && !isInitialized) {
      initializeContext()
    }
  }, [isAuthenticated, isInitialized, initializeContext])

  const handleSelectOrganization = (org: Organization) => {
    if (org.id !== currentOrganization?.id) {
      setCurrentOrganization(org)
    }
  }

  const handleSelectProject = (project: Project | null) => {
    setCurrentProject(project)
  }

  // Get display text
  const getDisplayText = () => {
    if (isLoading) {
      return t('messages.loading')
    }
    if (!currentOrganization) {
      return t('organization.selectOrganization')
    }
    if (currentProject) {
      return `${currentOrganization.name} > ${currentProject.name}`
    }
    return currentOrganization.name
  }

  // Get project icon/color indicator
  const getProjectIndicator = (project: Project) => {
    if (project.settings?.icon) {
      return <span className="text-base">{project.settings.icon}</span>
    }
    if (project.settings?.color) {
      return (
        <span
          className="h-3 w-3 rounded-full"
          style={{ backgroundColor: project.settings.color }}
        />
      )
    }
    return <FolderKanban className="h-4 w-4" />
  }

  if (!isAuthenticated) {
    return null
  }

  // Check if current user can create projects (owner or admin)
  const canCreateProject =
    currentOrganization?.user_role === 'owner' || currentOrganization?.user_role === 'admin'

  return (
    <>
      <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          // `min-w-[200px]` fixo empurrava o título para fora no mobile: com
          // 375px, o botão de menu, o nome da organização, o sino e o avatar
          // não cabem juntos. Abaixo de `sm` ele encolhe e o nome trunca.
          className="justify-between gap-2 max-w-[40vw] sm:min-w-[200px] sm:max-w-none"
          disabled={isLoading}
        >
          <div className="flex items-center gap-2 truncate">
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Building2 className="h-4 w-4 shrink-0" />
            )}
            {/* No mobile fica só o ícone: o nome da organização e o título da
                tela não cabem juntos em 375px, e saber ONDE se está vale mais
                que saber de quem é — o nome reaparece ao abrir o seletor. */}
            <span className="hidden truncate sm:inline">{getDisplayText()}</span>
          </div>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-[280px]" align="start">
        {/* Organizations Section */}
        <DropdownMenuLabel className="text-xs text-muted-foreground uppercase tracking-wider">
          {t('organization.organizations')}
        </DropdownMenuLabel>
        <DropdownMenuGroup>
          {organizations.length === 0 ? (
            <DropdownMenuItem disabled>
              <span className="text-muted-foreground">{t('organization.noOrganization')}</span>
            </DropdownMenuItem>
          ) : (
            organizations.map((org) => (
              <DropdownMenuItem
                key={org.id}
                onClick={() => handleSelectOrganization(org)}
                className="cursor-pointer"
              >
                <Building2 className="mr-2 h-4 w-4" />
                <span className="flex-1 truncate">{org.name}</span>
                {currentOrganization?.id === org.id && (
                  <Check className="ml-2 h-4 w-4" />
                )}
              </DropdownMenuItem>
            ))
          )}
        </DropdownMenuGroup>

        {/* Projects Section */}
        {currentOrganization && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-muted-foreground uppercase tracking-wider">
              {t('organization.projects')}
            </DropdownMenuLabel>
            <DropdownMenuGroup>
              {/* "All Projects" option */}
              <DropdownMenuItem
                onClick={() => handleSelectProject(null)}
                className="cursor-pointer"
              >
                <FolderKanban className="mr-2 h-4 w-4" />
                <span className="flex-1 truncate">{t('organization.allProjects')}</span>
                {currentProject === null && (
                  <Check className="ml-2 h-4 w-4" />
                )}
              </DropdownMenuItem>

              {projects.length === 0 ? (
                <DropdownMenuItem disabled>
                  <span className="text-muted-foreground ml-6">
                    {t('organization.noProject')}
                  </span>
                </DropdownMenuItem>
              ) : (
                projects.map((project) => (
                  <DropdownMenuItem
                    key={project.id}
                    onClick={() => handleSelectProject(project)}
                    className="cursor-pointer"
                  >
                    <span className="mr-2 flex h-4 w-4 items-center justify-center">
                      {getProjectIndicator(project)}
                    </span>
                    <span className="flex-1 truncate">{project.name}</span>
                    {currentProject?.id === project.id && (
                      <Check className="ml-2 h-4 w-4" />
                    )}
                  </DropdownMenuItem>
                ))
              )}
            </DropdownMenuGroup>

            {/* New Project button - only for owners and admins */}
            {canCreateProject && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => setShowCreateProject(true)}
                  className="cursor-pointer"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  <span className="flex-1 truncate">{tOrg('project.create.newProject')}</span>
                </DropdownMenuItem>
              </>
            )}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>

    <CreateProjectDialog open={showCreateProject} onOpenChange={setShowCreateProject} />
  </>
  )
}
